import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { REPORT_TEMPLATE_RESOLUTION, type ReportLanguage } from '@lims/shared-types';
import { prepareReportSchema } from '@lims/shared-validation';
import {
  reportTemplatesApi,
  reportsApi,
  workOrdersApi,
  type EligibleResultView,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  Dialog,
  EmptyState,
  LoadingState,
  PageHeader,
  ReadOnlyField,
  SelectField,
  TextAreaField,
  useToast,
} from '../../components/ui/index.js';
import { OnSurface } from '../../components/ui/surface.js';
import { currentLocale } from '../../i18n/index.js';
import { ResultSelection } from './ResultSelection.js';
import {
  TemplateLanguageFields,
  attemptDescriber,
  fittingLanguage,
  useActiveTemplates,
} from './report-draft-actions.js';
import { REPORTS_QUERY_KEY, ReportRefusalNotice, bilingual } from './report-parts.js';

/** The shared schema's limit on remarks. */
const MAXIMUM_REMARKS = 4000;

/**
 * CREATING A REPORT — Phase 10 (D113, D124, D125), with direct issue (D330).
 *
 * ===========================================================================
 * TWO ACTIONS, ONE SET OF RULES. A DRAFT HAS NO NUMBER.
 * ===========================================================================
 *
 * The page chooses approved results of ONE work order, the template and the
 * default language, then either SAVES A DRAFT (nothing numbered, rendered,
 * stored or sent; it is issued later on the report's own page) or ISSUES THE
 * REPORT now. Issuing here is the server's one transaction — the same
 * preparation and issuance rules as the two steps — so a refusal leaves no
 * report at all, and the form keeps everything that was entered.
 *
 * The language is the one the report is issued and previewed in. It never
 * limits the downloads: an issued report downloads in Arabic and in English.
 *
 * Only what the server offers can be chosen, and every rule is the server's:
 * a result no longer approved, a result already in a live report, a partial
 * report where the laboratory forbids one, a withdrawn template or an
 * unsupported language are each refused there and said here in words.
 *
 * Reaching the page needs `reports.prepare`, re-checked by the server.
 */
export function ReportPreparePage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const [params, setParams] = useSearchParams();
  const workOrderId = params.get('workOrderId');

  const [attemptIds, setAttemptIds] = useState<string[]>([]);
  const [templateId, setTemplateId] = useState<string | null>(null);
  // Once the preparer picks a template, the server's suggestion never overrides it.
  const [templateChosen, setTemplateChosen] = useState(false);
  const [language, setLanguage] = useState<ReportLanguage | null>(null);
  const [remarks, setRemarks] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [confirmIssue, setConfirmIssue] = useState(false);

  const mayReadWorkOrders = can(PERMISSIONS.WORK_ORDERS_VIEW);
  // A hint only: the server re-checks both permissions on direct issue.
  const mayIssue = can(PERMISSIONS.REPORTS_ISSUE);

  const workOrders = useQuery({
    queryKey: ['work-orders', 'report-prepare'],
    queryFn: () => workOrdersApi.list({ pageSize: 100 }),
    enabled: workOrderId === null && mayReadWorkOrders,
  });
  const workOrder = useQuery({
    queryKey: ['work-order', workOrderId],
    queryFn: () => workOrdersApi.get(workOrderId as string),
    enabled: workOrderId !== null && mayReadWorkOrders,
  });
  const eligible = useQuery({
    queryKey: [...REPORTS_QUERY_KEY, 'eligible', workOrderId],
    queryFn: () => reportsApi.eligibleResults(workOrderId as string),
    enabled: workOrderId !== null,
  });
  const templates = useActiveTemplates();
  // The laboratory's partial-report rule arrives WITH the eligible results, so
  // every preparer is told it — a supervisor prepares reports without holding
  // `settings.view`, and used to meet the rule only as the server's refusal.
  const allowPartialReports = eligible.data?.meta?.allowPartialReports;

  const templateRows = templates.data?.data;
  const activeTemplates = templateRows ?? [];

  // The laboratory's default template, preselected once the list arrives.
  useEffect(() => {
    if (templateId !== null || !templateRows) return;
    const preferred = templateRows.find((template) => template.isDefault);
    if (!preferred) return;
    setTemplateId(preferred.id);
    setLanguage((held) => fittingLanguage(preferred, held));
  }, [templateRows, templateId]);

  // THE RESOLUTION ORDER IS THE SERVER'S (D254): the tests' own template,
  // otherwise the laboratory default. The page asks rather than re-deciding.
  const resolution = useQuery({
    queryKey: ['report-templates', 'resolution', [...attemptIds].sort().join(',')],
    queryFn: () => reportTemplatesApi.resolution(attemptIds),
    enabled: attemptIds.length > 0 && !templateChosen,
  });
  const resolved = resolution.data;
  useEffect(() => {
    if (templateChosen || !resolved || !templateRows) return;
    const suggested = templateRows.find((template) => template.id === resolved.templateId);
    if (!suggested) {
      // Different templates and no default: the preparer chooses.
      if (resolved.source === REPORT_TEMPLATE_RESOLUTION.CONFLICT) setTemplateId(null);
      return;
    }
    setTemplateId(suggested.id);
    setLanguage((held) => fittingLanguage(suggested, held));
  }, [resolved, templateRows, templateChosen]);
  const resolutionHint =
    templateChosen || !resolved
      ? null
      : resolved.source === REPORT_TEMPLATE_RESOLUTION.CONFLICT
        ? resolved.templateId !== null && resolved.templateId === templateId
          ? t('reports.prepare.templateConflict')
          : resolved.templateId === null
            ? t('reports.prepare.templateConflictNoDefault')
            : null
        : resolved.source === REPORT_TEMPLATE_RESOLUTION.TEST_SPECIFIC && resolved.templateId === templateId
          ? t('reports.prepare.templateResolvedTest')
          : resolved.source === REPORT_TEMPLATE_RESOLUTION.LABORATORY_DEFAULT && resolved.templateId === templateId
            ? t('reports.prepare.templateResolvedDefault')
            : null;

  const prepare = useMutation({
    mutationFn: (body: Parameters<typeof reportsApi.prepare>[0]) => reportsApi.prepare(body),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEY });
      // From the server's answer: a draft exists, and nothing was issued.
      show({ tone: 'success', message: t('reports.prepare.created') });
      navigate(`/reports/${created.id}`);
    },
  });
  const issueNow = useMutation({
    mutationFn: (body: Parameters<typeof reportsApi.prepareAndIssue>[0]) =>
      reportsApi.prepareAndIssue(body),
    onSuccess: async (issued) => {
      await queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEY });
      // From the server's answer: issued — and, in the same breath, not delivered.
      show({ tone: 'success', message: t('reports.detail.issued') });
      setConfirmIssue(false);
      navigate(`/reports/${issued.id}`);
    },
  });
  const busy = prepare.isPending || issueNow.isPending;

  const eligibleRows: readonly EligibleResultView[] = eligible.data?.data ?? [];
  const template = activeTemplates.find((candidate) => candidate.id === templateId);
  const trimmedRemarks = remarks.trim();
  const remarksError = trimmedRemarks.length > MAXIMUM_REMARKS ? t('validation.tooLong') : undefined;
  const selectionError = attemptIds.length === 0 ? t('validation.required') : undefined;

  /** The body both actions send, or null while the form is incomplete. */
  const readyBody = () => {
    setShowErrors(true);
    if (!workOrderId || selectionError || remarksError || !template || !language) return null;
    if (!template.languages.includes(language)) return null;
    const body = {
      workOrderId,
      templateId: template.id,
      language,
      attemptIds,
      ...(trimmedRemarks ? { remarks: trimmedRemarks } : {}),
    };
    // The schema the API validates with. A body it refuses is never sent.
    return prepareReportSchema.safeParse(body).success ? body : null;
  };
  const saveDraft = () => {
    const body = readyBody();
    if (body) prepare.mutate(body);
  };
  const askToIssue = () => {
    if (readyBody()) {
      issueNow.reset();
      setConfirmIssue(true);
    }
  };

  const header = (
    <PageHeader
      title={t('reports.prepare.title')}
      subtitle={t('reports.prepare.subtitle')}
      breadcrumbs={[
        { label: t('nav.reports'), to: '/reports' },
        { label: t('reports.prepare.title') },
      ]}
    />
  );

  // No work order chosen yet: a report covers exactly one.
  if (workOrderId === null) {
    return (
      <>
        {header}
        <PrepareStep number={1} title={t('reports.prepare.workOrder')} description={t('reports.prepare.workOrderHint')}>
          {mayReadWorkOrders ? (
            workOrders.error ? (
              <ErrorNotice error={workOrders.error} />
            ) : (
              <div className="w-full sm:w-80">
                <SelectField
                  label={t('reports.prepare.workOrder')}
                  placeholder={t('common.notSet')}
                  value={null}
                  isRequired
                  onChange={(value) => {
                    if (value) setParams({ workOrderId: value });
                  }}
                  options={(workOrders.data?.data ?? []).map((order) => ({
                    value: order.id,
                    label: order.workOrderNumber,
                  }))}
                />
              </div>
            )
          ) : (
            <EmptyState title={t('reports.prepare.workOrderHint')} />
          )}
        </PrepareStep>
      </>
    );
  }

  return (
    <>
      {header}

      {/*
        Four steps in the order the work is done (D278): the work order, the
        results it prints, the template and language, then the outline with the
        one action. On a wide screen the outline stays beside the steps.
      */}
      <div className="grid items-start gap-gutter lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex min-w-0 flex-col gap-gutter">
          <PrepareStep
            number={1}
            title={t('reports.prepare.workOrder')}
            description={t('reports.prepare.workOrderHint')}
          >
            {workOrder.data ? (
              <ReadOnlyField label={t('reports.prepare.workOrder')}>
                <span className="measure" dir="ltr">
                  {workOrder.data.workOrderNumber}
                </span>
              </ReadOnlyField>
            ) : null}
            {allowPartialReports !== undefined ? (
              allowPartialReports ? (
                <p className="mt-stack text-caption text-default-600">
                  {t('reports.prepare.partialAllowedHint')}
                </p>
              ) : (
                <div className="mt-stack">
                  <Alert
                    tone="warning"
                    title={t('reports.prepare.partialTitle')}
                    description={t('reports.prepare.partialBody')}
                  />
                </div>
              )
            ) : null}
          </PrepareStep>

          <PrepareStep number={2} title={t('reports.prepare.steps.results')}>
            {eligible.isLoading ? (
              <LoadingState />
            ) : eligible.error ? (
              <ErrorNotice error={eligible.error} />
            ) : (
              <ResultSelection
                eligible={eligibleRows}
                selected={attemptIds}
                onChange={setAttemptIds}
                {...(showErrors && selectionError ? { error: selectionError } : {})}
              />
            )}
          </PrepareStep>

          <PrepareStep number={3} title={t('reports.prepare.steps.template')}>
            <div className="flex flex-col gap-stack">
              {templates.isLoading ? (
                <LoadingState />
              ) : templates.error ? (
                <ErrorNotice error={templates.error} />
              ) : activeTemplates.length === 0 ? (
                <Alert tone="warning" title={t('reports.prepare.noTemplate')} />
              ) : (
                <>
                  <TemplateLanguageFields
                    templates={activeTemplates}
                    templateId={templateId}
                    language={language}
                    onTemplateChange={(value) => {
                      setTemplateChosen(true);
                      setTemplateId(value);
                    }}
                    onLanguageChange={setLanguage}
                    showErrors={showErrors}
                  />
                  {resolutionHint ? (
                    <p className="-mt-2 text-caption text-default-600" data-testid="template-resolution">
                      {resolutionHint}
                    </p>
                  ) : null}
                </>
              )}
              <TextAreaField
                label={t('reports.prepare.remarks')}
                description={t('reports.prepare.remarksHint')}
                value={remarks}
                onChange={setRemarks}
                rows={3}
                {...(remarksError ? { error: remarksError } : {})}
              />
            </div>
          </PrepareStep>
        </div>

        <div className="flex min-w-0 flex-col gap-gutter lg:sticky lg:top-4">
          <PrepareStep
            number={4}
            title={t('reports.prepare.steps.outline')}
            description={t('reports.prepare.steps.outlineHint')}
          >
            <div className="flex flex-col gap-stack">
              <dl className="grid gap-2 text-body">
                <OutlineFact label={t('reports.prepare.workOrder')}>
                  {workOrder.data ? (
                    <span className="measure" dir="ltr">
                      {workOrder.data.workOrderNumber}
                    </span>
                  ) : (
                    t('common.notSet')
                  )}
                </OutlineFact>
                <OutlineFact label={t('reports.prepare.steps.results')}>
                  <span className="measure">{attemptIds.length}</span>
                </OutlineFact>
                <OutlineFact label={t('reports.prepare.template')}>
                  {template ? (
                    <bdi>{bilingual(template.name, locale) ?? t('common.notRecorded')}</bdi>
                  ) : (
                    t('common.notSet')
                  )}
                </OutlineFact>
                <OutlineFact label={t('reports.prepare.language')}>
                  {language ? t(`reportLanguage.${language}`) : t('common.notSet')}
                </OutlineFact>
              </dl>

              <Alert
                tone="info"
                title={t('reports.prepare.twoActionsTitle')}
                description={
                  mayIssue ? t('reports.prepare.twoActionsBody') : t('reports.prepare.draftOnlyBody')
                }
              />

              {prepare.error ? (
                <ReportRefusalNotice
                  error={prepare.error}
                  describeAttempt={attemptDescriber(eligibleRows)}
                />
              ) : null}

              <ActionBar>
                <ActionButton
                  priority="tertiary"
                  onPress={() => navigate('/reports')}
                  isDisabled={busy}
                >
                  {t('common.cancel')}
                </ActionButton>
                <ActionButton
                  priority={mayIssue ? 'secondary' : 'primary'}
                  isLoading={prepare.isPending}
                  isDisabled={issueNow.isPending}
                  onPress={saveDraft}
                >
                  {t('reports.prepare.saveDraftAction')}
                </ActionButton>
                {mayIssue ? (
                  <ActionButton priority="primary" isDisabled={prepare.isPending} onPress={askToIssue}>
                    {t('reports.prepare.issueAction')}
                  </ActionButton>
                ) : null}
              </ActionBar>
            </div>
          </PrepareStep>
        </div>
      </div>

      {confirmIssue ? (
        <Dialog
          isOpen
          onClose={() => {
            if (!issueNow.isPending) setConfirmIssue(false);
          }}
          size="lg"
          title={t('reports.prepare.issueAction')}
          footer={
            <ActionBar>
              <ActionButton
                priority="tertiary"
                onPress={() => setConfirmIssue(false)}
                isDisabled={issueNow.isPending}
              >
                {t('common.cancel')}
              </ActionButton>
              <ActionButton
                priority="primary"
                isLoading={issueNow.isPending}
                onPress={() => {
                  const body = readyBody();
                  if (body) issueNow.mutate(body);
                }}
              >
                {t('reports.prepare.issueAction')}
              </ActionButton>
            </ActionBar>
          }
        >
          <div className="flex flex-col gap-stack">
            <Alert
              tone="warning"
              title={t('reports.detail.issueWarningTitle')}
              description={t('reports.detail.issueWarningBody')}
            />
            <Alert
              tone="info"
              title={t('reports.issuanceNotDeliveryTitle')}
              description={t('reports.issuanceNotDeliveryBody')}
            />
            {issueNow.isPending ? (
              <p className="text-caption text-default-600" aria-live="polite">
                {t('reports.detail.issuing')}
              </p>
            ) : null}
            {issueNow.error ? (
              <>
                <ReportRefusalNotice
                  error={issueNow.error}
                  describeAttempt={attemptDescriber(eligibleRows)}
                />
                <p className="text-caption text-default-600">{t('reports.prepare.nothingWasIssued')}</p>
              </>
            ) : null}
          </div>
        </Dialog>
      ) : null}
    </>
  );
}

/**
 * One numbered step of the preparation (D278). The number is drawn, and also
 * spoken as "Step n" before the title, so the order is not carried by a
 * picture alone.
 */
function PrepareStep({
  number,
  title,
  description,
  children,
}: {
  readonly number: number;
  /** Already translated. */
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className="min-w-0 rounded-large border border-default-200 bg-white"
    >
      <header className="flex items-start gap-3 border-b border-default-200 px-4 py-2.5">
        <span
          aria-hidden="true"
          className="measure mt-px inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-50 text-caption font-bold text-primary-700"
        >
          {number}
        </span>
        <div className="min-w-0 flex-1">
          <h2 id={headingId} className="text-body font-bold text-default-900">
            <span className="sr-only">{t('reports.prepare.steps.label', { number })} </span>
            {title}
          </h2>
          {description ? <p className="text-caption text-default-600">{description}</p> : null}
        </div>
      </header>
      <div className="p-4">
        <OnSurface>{children}</OnSurface>
      </div>
    </section>
  );
}

/** One line of the outline: a caption and the choice made so far. */
function OutlineFact({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-stack">
      <dt className="text-caption text-default-600">{label}</dt>
      <dd className="min-w-0 text-end text-default-900">{children}</dd>
    </div>
  );
}
